import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box, Typography, Card, CardContent, Button, FormControl, InputLabel, Select,
  MenuItem, Chip, Alert, Snackbar,
  IconButton, Dialog, DialogTitle, DialogContent, DialogActions, LinearProgress
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { Save, Delete, CheckCircle, Cancel, ArrowBack, Mic, Add, AutoAwesome } from '@mui/icons-material';
import dayjs from 'dayjs';
import { api } from '../api';

export default function AttendancePage() {
  const { date: urlDate } = useParams();
  const [searchParams] = useSearchParams();
  const preselectedClassId = searchParams.get('classId') || '';
  const navigate = useNavigate();
  const [selectedDate, setSelectedDate] = useState(urlDate ? dayjs(urlDate) : dayjs());
  const [classes, setClasses] = useState([]);
  const [selectedClassId, setSelectedClassId] = useState(preselectedClassId);
  const [enrolledStudents, setEnrolledStudents] = useState([]);
  const [attendanceMap, setAttendanceMap] = useState({});
  const [existingSessions, setExistingSessions] = useState([]);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [loading, setLoading] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [uploadingSessionId, setUploadingSessionId] = useState(null);
  const [uploadProgress, setUploadProgress] = useState({});
  const [sessionRecordings, setSessionRecordings] = useState({});
  const [transcribingId, setTranscribingId] = useState(null);
  const fileInputRef = useRef({});
  const addFileInputRef = useRef({});

  const dateStr = selectedDate.format('YYYY-MM-DD');

  useEffect(() => {
    api.getClasses().then(data => setClasses(data.filter(c => c.is_active)));
  }, []);

  const fetchDateSessions = useCallback(async () => {
    try {
      const sessions = await api.getDateSessions(dateStr);
      setExistingSessions(sessions);
      // Fetch recordings for each session in parallel
      const recMap = {};
      await Promise.all(sessions.map(async s => {
        try {
          const recs = await api.getRecordingsBySession(s.id);
          recMap[s.id] = recs;
        } catch (_) { recMap[s.id] = []; }
      }));
      setSessionRecordings(recMap);
    } catch (err) {
      console.error(err);
    }
  }, [dateStr]);

  useEffect(() => { fetchDateSessions(); }, [fetchDateSessions]);

  useEffect(() => {
    if (!selectedClassId) {
      setEnrolledStudents([]);
      setAttendanceMap({});
      return;
    }
    const fetchClassStudents = async () => {
      try {
        const cls = await api.getClass(selectedClassId);
        setEnrolledStudents(cls.students || []);
        
        // Check if there's an existing session for this class on this date
        const existingSession = existingSessions.find(s => s.class_id === selectedClassId);
        if (existingSession) {
          const map = {};
          existingSession.attendance.forEach(a => {
            map[a.student_id] = a.status;
          });
          setAttendanceMap(map);
        } else {
          // Default all enrolled students to present
          const map = {};
          (cls.students || []).forEach(s => { map[s.id] = 'present'; });
          setAttendanceMap(map);
        }
      } catch (err) {
        console.error(err);
      }
    };
    fetchClassStudents();
  }, [selectedClassId, existingSessions]);

  const toggleAttendance = (studentId) => {
    setAttendanceMap(prev => ({
      ...prev,
      [studentId]: prev[studentId] === 'present' ? 'absent' : 'present'
    }));
  };

  const handleSave = async () => {
    if (!selectedClassId) return;
    setLoading(true);
    try {
      const attendanceList = enrolledStudents.map(s => ({
        student_id: s.id,
        status: attendanceMap[s.id] || 'absent'
      }));
      await api.createSession({
        class_id: selectedClassId,
        session_date: dateStr,
        attendance: attendanceList
      });
      setSnackbar({ open: true, message: 'Attendance saved!', severity: 'success' });
      fetchDateSessions();
    } catch (err) {
      setSnackbar({ open: true, message: err.message, severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteSession = async (sessionId) => {
    try {
      await api.deleteSession(sessionId);
      setSnackbar({ open: true, message: 'Session deleted', severity: 'info' });
      setConfirmDelete(null);
      fetchDateSessions();
    } catch (err) {
      setSnackbar({ open: true, message: err.message, severity: 'error' });
    }
  };

  const markAll = (status) => {
    const map = {};
    enrolledStudents.forEach(s => { map[s.id] = status; });
    setAttendanceMap(map);
  };

  const handleUploadRecording = async (session, file, replace = null) => {
    if (!file) return;
    setUploadingSessionId(session.id);
    setUploadProgress(p => ({ ...p, [session.id]: 'uploading' }));
    try {
      // If replacing a specific recording, delete it first
      if (replace) {
        await api.deleteRecording(replace.id);
      }

      const { uploadUrl, recording } = await api.getRecordingUploadUrl({
        sessionId: session.id,
        classId: session.class_id,
        sessionDate: session.session_date,
        filename: file.name,
        contentType: file.type || 'audio/mp4',
      });
      await api.uploadToS3(uploadUrl, file);

      // Update local state: replace old or append new
      setSessionRecordings(prev => {
        const existing = (prev[session.id] || []).filter(r => !replace || r.id !== replace.id);
        return { ...prev, [session.id]: [...existing, recording] };
      });
      setUploadProgress(p => ({ ...p, [session.id]: 'done' }));
      setSnackbar({ open: true, message: 'Recording uploaded! Will be transcribed Sunday night.', severity: 'success' });
    } catch (err) {
      setUploadProgress(p => ({ ...p, [session.id]: 'error' }));
      setSnackbar({ open: true, message: err.message, severity: 'error' });
    } finally {
      setUploadingSessionId(null);
    }
  };

  const handleTranscribeNow = async (rec, sessionId) => {
    setTranscribingId(rec.id);
    // Mark as processing in local state immediately
    setSessionRecordings(prev => ({
      ...prev,
      [sessionId]: (prev[sessionId] || []).map(r =>
        r.id === rec.id ? { ...r, processingStatus: 'processing' } : r
      ),
    }));
    try {
      await api.transcribeRecording(rec.id);
      setSnackbar({ open: true, message: 'Transcription started — checking every 15s...', severity: 'info' });

      // Poll every 15s for up to 3 minutes
      let attempts = 0;
      const poll = setInterval(async () => {
        attempts++;
        try {
          const recs = await api.getRecordingsBySession(sessionId);
          const updated = recs.find(r => r.id === rec.id);
          if (updated && (updated.processingStatus === 'done' || updated.processingStatus === 'error')) {
            clearInterval(poll);
            setTranscribingId(null);
            setSessionRecordings(prev => ({
              ...prev,
              [sessionId]: (prev[sessionId] || []).map(r => r.id === rec.id ? { ...r, ...updated } : r),
            }));
            if (updated.processingStatus === 'done') {
              setSnackbar({ open: true, message: 'Transcription complete!', severity: 'success' });
            } else {
              setSnackbar({ open: true, message: `Transcription failed: ${updated.errorMessage}`, severity: 'error' });
            }
          }
        } catch (_) {}
        if (attempts >= 12) { // 3 min max
          clearInterval(poll);
          setTranscribingId(null);
          setSnackbar({ open: true, message: 'Transcription is taking longer than expected — check back later.', severity: 'warning' });
        }
      }, 15000);
    } catch (err) {
      setTranscribingId(null);
      setSnackbar({ open: true, message: `Failed to start transcription: ${err.message}`, severity: 'error' });
    }
  };

  return (
    <Box sx={{ p: 2, maxWidth: 600, mx: 'auto' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        {urlDate && (
          <IconButton onClick={() => navigate('/')}><ArrowBack /></IconButton>
        )}
        <Typography variant="h5" fontWeight={700}>Mark Attendance</Typography>
      </Box>

      {/* Date Picker */}
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <DatePicker
            label="Select Date"
            value={selectedDate}
            onChange={(val) => {
              setSelectedDate(val);
              setSelectedClassId('');
            }}
            sx={{ width: '100%' }}
            slotProps={{ textField: { fullWidth: true } }}
          />
        </CardContent>
      </Card>

      {/* Existing Sessions for this date */}
      {existingSessions.length > 0 && (
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
            Sessions on {dateStr}
          </Typography>
          {existingSessions.map(session => (
            <Card key={session.id} sx={{ mb: 1 }}>
              <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography fontWeight={600}>{session.class_name}</Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                      {session.attendance.map(a => (
                        <Chip
                          key={a.student_id}
                          label={a.student_name}
                          size="small"
                          color={a.status === 'present' ? 'success' : 'error'}
                          variant="outlined"
                          icon={a.status === 'present' ? <CheckCircle /> : <Cancel />}
                        />
                      ))}
                    </Box>
                  </Box>
                  <IconButton size="small" color="error" onClick={() => setConfirmDelete(session.id)}>
                    <Delete fontSize="small" />
                  </IconButton>
                </Box>

                {/* Recording section */}
                <Box sx={{ mt: 1.5, pt: 1.5, borderTop: '1px solid #F2F4F7' }}>
                  {/* Hidden file input for adding a new recording */}
                  <input
                    type="file"
                    accept="audio/*,.m4a,.mp3,.wav,.aac"
                    style={{ display: 'none' }}
                    ref={el => { addFileInputRef.current[session.id] = el; }}
                    onChange={e => {
                      handleUploadRecording(session, e.target.files[0]);
                      e.target.value = '';
                    }}
                  />

                  {uploadProgress[session.id] === 'uploading' ? (
                    <Box>
                      <Typography variant="caption" color="text.secondary">Uploading...</Typography>
                      <LinearProgress sx={{ mt: 0.5, borderRadius: 1 }} />
                    </Box>
                  ) : (() => {
                    const recs = sessionRecordings[session.id] || [];
                    return (
                      <Box>
                        {/* Existing recordings */}
                        {recs.map((rec, idx) => (
                          <Box key={rec.id} sx={{ mb: 1 }}>
                            {/* Hidden replace input per recording */}
                            <input
                              type="file"
                              accept="audio/*,.m4a,.mp3,.wav,.aac"
                              style={{ display: 'none' }}
                              ref={el => { fileInputRef.current[rec.id] = el; }}
                              onChange={e => {
                                handleUploadRecording(session, e.target.files[0], rec);
                                e.target.value = '';
                              }}
                            />
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
                              <Typography variant="caption" fontWeight={600} color="#5B6BA8">
                                <Mic sx={{ fontSize: 13, mr: 0.5, verticalAlign: 'middle' }} />
                                Recording {recs.length > 1 ? idx + 1 : ''}
                                {rec.processingStatus === 'done' && ' · Transcribed ✓'}
                                {rec.processingStatus === 'processing' && ' · Transcribing...'}
                                {rec.processingStatus === 'pending' && ' · Pending'}
                              </Typography>
                              <Box sx={{ display: 'flex', gap: 0.5 }}>
                                {rec.processingStatus !== 'done' && (
                                  <Button
                                    size="small" variant="text" startIcon={<AutoAwesome sx={{ fontSize: 12 }} />}
                                    onClick={() => handleTranscribeNow(rec, session.id)}
                                    disabled={transcribingId === rec.id}
                                    sx={{ fontSize: '0.7rem', color: '#6366f1', minWidth: 0, p: '2px 6px' }}
                                  >
                                    {transcribingId === rec.id ? 'Transcribing...' : 'Transcribe Now'}
                                  </Button>
                                )}
                                <Button
                                  size="small" variant="text"
                                  onClick={() => fileInputRef.current[rec.id]?.click()}
                                  sx={{ fontSize: '0.7rem', color: '#98A2B3', minWidth: 0, p: '2px 6px' }}
                                >
                                  Replace
                                </Button>
                              </Box>
                            </Box>
                            {rec.audioUrl && (
                              <audio controls src={rec.audioUrl} style={{ width: '100%', height: 36 }} />
                            )}
                            {rec.summary && (
                              <Box sx={{ mt: 0.75, p: 1, borderRadius: 1.5, bgcolor: '#F8F9FF', border: '1px solid #E0E4FF' }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                                  <AutoAwesome sx={{ fontSize: 12, color: '#6366f1' }} />
                                  <Typography variant="caption" fontWeight={700} color="#4f46e5">Summary</Typography>
                                </Box>
                                <Typography variant="caption" color="#344054" sx={{ lineHeight: 1.5 }}>
                                  {rec.summary}
                                </Typography>
                              </Box>
                            )}
                          </Box>
                        ))}

                        {/* Add recording button */}
                        <Button
                          size="small" variant="outlined" startIcon={recs.length === 0 ? <Mic /> : <Add />}
                          onClick={() => addFileInputRef.current[session.id]?.click()}
                          disabled={uploadingSessionId === session.id}
                          sx={{ borderColor: '#93A3D8', color: '#5B6BA8', fontSize: '0.75rem', mt: recs.length > 0 ? 0.5 : 0 }}
                        >
                          {recs.length === 0 ? 'Upload Recording' : '+ Add Recording'}
                        </Button>
                      </Box>
                    );
                  })()}
                </Box>
              </CardContent>
            </Card>
          ))}
        </Box>
      )}

      {/* Class Selection */}
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <FormControl fullWidth>
            <InputLabel>Select Class</InputLabel>
            <Select
              value={selectedClassId}
              onChange={e => setSelectedClassId(e.target.value)}
              label="Select Class"
            >
              {classes.map(cls => (
                <MenuItem key={cls.id} value={cls.id}>
                  {cls.name}
                  {existingSessions.some(s => s.class_id === cls.id) && ' (already recorded)'}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </CardContent>
      </Card>

      {/* Attendance Marking */}
      {selectedClassId && enrolledStudents.length > 0 && (
        <Card sx={{ mb: 2 }}>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="subtitle1" fontWeight={600}>
                Students ({enrolledStudents.length})
              </Typography>
              <Box sx={{ display: 'flex', gap: 0.5 }}>
                <Chip label="All Present" size="small" color="success" variant="outlined"
                  onClick={() => markAll('present')} clickable />
                <Chip label="All Absent" size="small" color="error" variant="outlined"
                  onClick={() => markAll('absent')} clickable />
              </Box>
            </Box>

            {enrolledStudents.map(student => {
              const status = attendanceMap[student.id] || 'absent';
              const isPresent = status === 'present';
              return (
                <Box
                  key={student.id}
                  onClick={() => toggleAttendance(student.id)}
                  sx={{
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    p: 1.5, mb: 1, borderRadius: 2, cursor: 'pointer',
                    border: '2px solid',
                    borderColor: isPresent ? 'success.main' : 'error.light',
                    bgcolor: isPresent ? 'rgba(16,185,129,0.05)' : 'rgba(239,68,68,0.05)',
                    transition: 'all 0.15s',
                    '&:hover': { transform: 'scale(1.01)' }
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {isPresent ? (
                      <CheckCircle color="success" />
                    ) : (
                      <Cancel color="error" />
                    )}
                    <Typography fontWeight={500}>{student.name}</Typography>
                  </Box>
                  <Chip
                    label={isPresent ? 'Present' : 'Absent'}
                    size="small"
                    color={isPresent ? 'success' : 'error'}
                  />
                </Box>
              );
            })}

            <Button
              fullWidth variant="contained" size="large" startIcon={<Save />}
              onClick={handleSave} disabled={loading}
              sx={{ mt: 2, py: 1.5 }}
            >
              {loading ? 'Saving...' : 'Save Attendance'}
            </Button>
          </CardContent>
        </Card>
      )}

      {selectedClassId && enrolledStudents.length === 0 && (
        <Alert severity="info">
          No students enrolled in this class. Go to Students page to enroll students.
        </Alert>
      )}

      {/* Delete Confirmation */}
      <Dialog open={!!confirmDelete} onClose={() => setConfirmDelete(null)}>
        <DialogTitle>Delete Session?</DialogTitle>
        <DialogContent>
          <Typography>This will remove all attendance records for this session. This cannot be undone.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDelete(null)}>Cancel</Button>
          <Button color="error" variant="contained" onClick={() => handleDeleteSession(confirmDelete)}>Delete</Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        message={snackbar.message}
      />
    </Box>
  );
}
